/**
 * Self-Cert Entity XML Generator - COMPLETE VERSION
 * Updated implementation with ALL form fields mapped systematically
 */

import { SelfCertEntityService } from "../../../services/SelfCertEntityService";
import { SelfCertEntityFormModel } from "../../../Models/SelfCertEntityModels";
import { escapeXmlText, getPDFBase64 } from "./generateW8BENXML";
import { getStateCode, parseSelfCertDate } from "../../../utils/formUtils";

export async function generateSelfCertEntityXML(data: any): Promise<string> {
  // Check if form data needs mapping
  let formModel: SelfCertEntityFormModel;
  let isMapDone = data?.isMapDone || false;

  if (!isMapDone) {
    formModel = SelfCertEntityService.fromApiResponse(data);
  } else {
    console.log("🧱 SelfCert Entity XML: Data already mapped, using directly");
    formModel = new SelfCertEntityFormModel(data);
  }

  // Helper function to get field values
  const getValue = (fieldName: string): string => {
    const value = formModel[fieldName] || "";
    return value;
  };

  // Thin adapter: reads the field value and delegates to the shared parseSelfCertDate.
  const formatSelfCertDate = (fieldName: string): string => parseSelfCertDate(getValue(fieldName));
  const convertYesNoToBoolean = (fieldName: string): string => {
    const value = formModel[fieldName];
    if (
      value === true ||
      value === "yes" ||
      value === "Yes" ||
      value === "selected" ||
      value === "true"
    ) {
      return "true";
    }
    if (
      value === false ||
      value === "no" ||
      value === "No" ||
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return "false";
    }
    return "false"; // Default to false for any other value
  };

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, formModel["fileImportId"]);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();

  const VALID_CAPACITY_OPTIONS = [
    "Attorney in Fact",
    "Beneficial Owner/Self",
    "Bursar",
    "Capacity Not Requested",
    "CEO",
    "CFO",
    "Custodian",
    "Director",
    "Executive Vice-President",
    "Executor",
    "Executrix",
    "Finance Director",
    "Guardian",
    "Managing Director",
    "Managing Member",
    "Managing Partner",
    "Member",
    "Parent",
    "Partner",
    "Power of Attorney",
    "President",
    "Provost",
    "Secretary of the Company",
    "Self",
    "Senior Partner",
    "Senior Vice-President",
    "Tax Matters Partner",
    "Treasurer",
    "Trustee",
    "Vice-President",
    "Other",
    "Unknown",
  ];

  const getCapacityValues = (fieldName: string): { capacity: string; capacityOther: string } => {
    const value = getValue(fieldName);
    if (!value || VALID_CAPACITY_OPTIONS.includes(value)) {
      return { capacity: value, capacityOther: "" };
    }
    return { capacity: "Other", capacityOther: value };
  };

  const { capacity: signCapacity, capacityOther: signCapacityOther } = getCapacityValues(
    "SC-Entity-Section_4_capacity"
  );

  // Generate XML using the latest 2024 template structure
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Deloitte_US_Entity_2024>
	<BusinessUnit>${getValue("BusinessUnit")}</BusinessUnit>
	<UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
	<CommonInfo>
		<Business>
			<Name>${getValue("SC-Entity-Section_1_entity_name")}</Name>
			<Country>${getValue("SC-Entity-Section_1_org_country")}</Country>
			<CountryOther>${getValue("Other")}</CountryOther>
		</Business>
		<PermanentAddress>
			<Line1>${getValue("SC-Entity-Section_1_current_residence_address_street")}</Line1>
			<Line2>${getValue("SC-Entity-Section_1_current_residence_address_line2")}</Line2>
			<Line3>${getValue("SC-Entity-Section_1_current_residence_address_line3")}</Line3>
			<City_Town>${getValue("SC-Entity-Section_1_current_residence_address_city")}</City_Town>
			<State_Province>${getStateCode(getValue("SC-Entity-Section_1_current_residence_address_state"))}</State_Province>
			<Country>${getValue("SC-Entity-Section_1_current_residence_address_country")}</Country>
			<CountryOther></CountryOther>
			<Zip_PostalCode>${getValue("SC-Entity-Section_1_current_residence_address_postal")}</Zip_PostalCode>
			<IsCare_of_address>${convertYesNoToBoolean("SC-Entity-Section_1_current_residence_care_of")}</IsCare_of_address>
			<IsP_O_Box>${convertYesNoToBoolean("SC-Entity-Section_1_current_residence_po_box")}</IsP_O_Box>
			<IsRuralRoute>${convertYesNoToBoolean("SC-Entity-Section_1_current_residence_rural_route")}</IsRuralRoute>
		</PermanentAddress>
		<MailingAddress>
			<Line1>${getValue("SC-Entity-Section_1_mailing_address_street")}</Line1>
			<Line2>${getValue("SC-Entity-Section_1_mailing_address_line2")}</Line2>
			<Line3>${getValue("SC-Entity-Section_1_mailing_address_line3")}</Line3>
			<City_Town>${getValue("SC-Entity-Section_1_mailing_address_city")}</City_Town>
			<State_Province>${getStateCode(getValue("SC-Entity-Section_1_mailing_address_state"))}</State_Province>
			<Country>${getValue("SC-Entity-Section_1_mailing_address_country")}</Country>
			<CountryOther></CountryOther>
			<Zip_PostalCode>${getValue("SC-Entity-Section_1_mailing_address_postal")}</Zip_PostalCode>
			<IsCare_of_address>${convertYesNoToBoolean("SC-Entity-Section_1_mailing_care_of")}</IsCare_of_address>
			<IsP_O_Box>${convertYesNoToBoolean("SC-Entity-Section_1_mailing_po_box")}</IsP_O_Box>
			<IsRuralRoute>${convertYesNoToBoolean("SC-Entity-Section_1_mailing_rural_route")}</IsRuralRoute>
		</MailingAddress>
	</CommonInfo>
	<SignInfo>
		<IsSigned>${convertYesNoToBoolean("SC-Entity-isSigned")}</IsSigned>
		<SignatureUnreadable>${convertYesNoToBoolean("SC-Entity-Section_4_Signature_unreadable")}</SignatureUnreadable>
		<By>${getValue("SC-Entity-Section_4_signer_printed_name")}</By>
		<Capacity>${signCapacity}</Capacity>
		<CapacityOther>${signCapacityOther}</CapacityOther>
		<Date>${formatSelfCertDate("SC-Entity-Section_4_signature_date")}</Date> 
		<DateOnForm>${getValue("Signature_date_on_form") || getValue("SC-Entity-Section_4_signature_date")}</DateOnForm>       	
	</SignInfo>			
	<TaxResidencyCountry>${getValue("SC-Entity-Section_2_tax_residence") || getValue("SC-Entity-Section_2_tax_identification_country")}</TaxResidencyCountry>
  	<TaxResidencyTINType>${getValue("SC-Entity-Section_2_tax_reference_number_type")}</TaxResidencyTINType>
  	<TaxResidencyTIN>${getValue("SC-Entity-Section_2_tax_id")}</TaxResidencyTIN>
  	<cbTaxResidencyTINNotAvailable>${convertYesNoToBoolean("SC-Entity-Section_2_unable_to_procure_a_tin")}</cbTaxResidencyTINNotAvailable>
  	<TaxResidencyTINNotAvailableReason>${getValue("SC-Entity-Section_2_reason_for_non_tin")}</TaxResidencyTINNotAvailableReason>
  	<cbTaxResidencyYes>${convertYesNoToBoolean("SC-Entity-Section_2_sole_residency_yes")}</cbTaxResidencyYes>
  	<cbTaxResidencyNo>${convertYesNoToBoolean("SC-Entity-Section_2_sole_residency_no")}</cbTaxResidencyNo>
	<TaxJurisdictions>
		<TaxReferenceNoType>${getValue("sc_11 Tax Reference Number Type2")}</TaxReferenceNoType>
		<tjTaxID>${getValue("sc_11 non-availability of TIN 2")}</tjTaxID>
		<FATCATypeText>${getValue("sc_11 Tax Identification Number2")}</FATCATypeText>
		<taxCountry_ISOCode>${getValue("sc_11 Jurisdiction of Tax Residence2")}</taxCountry_ISOCode>
	</TaxJurisdictions>
	<TaxJurisdictions>
		<TaxReferenceNoType>${getValue("sc_11 Tax Reference Number Type3")}</TaxReferenceNoType>
		<tjTaxID>${getValue("sc_11 non-availability of TIN 3")}</tjTaxID>
		<FATCATypeText>${getValue("sc_11 Tax Identification Number3")}</FATCATypeText>
		<taxCountry_ISOCode>${getValue("sc_11 Jurisdiction of Tax Residence3")}</taxCountry_ISOCode>
	</TaxJurisdictions>
	<TaxJurisdictions>
		<TaxReferenceNoType>${getValue("sc_11 Tax Reference Number Type4")}</TaxReferenceNoType>
		<tjTaxID>${getValue("sc_11 non-availability of TIN 4")}</tjTaxID>
		<FATCATypeText>${getValue("sc_11 Tax Identification Number4")}</FATCATypeText>
		<taxCountry_ISOCode>${getValue("sc_11 Jurisdiction of Tax Residence4")}</taxCountry_ISOCode>
	</TaxJurisdictions>
	<IsCRSFI>${convertYesNoToBoolean("SC-Entity-Section_3_financial_institution")}</IsCRSFI>
	<TaxpayerIdentification_GIIN>${getValue("SC-Entity-Section_3_fatca_purposes")}</TaxpayerIdentification_GIIN>
	<IsCRSReportingFI>${convertYesNoToBoolean("SC-Entity-Section_3_reporting_financial_institution_under_crs")}</IsCRSReportingFI>
	<IsCRSNonReportingFI>${convertYesNoToBoolean("SC-Entity-Section_3_non_financial_institution_under_crs")}</IsCRSNonReportingFI>
	<IsCRSGovEntity>${convertYesNoToBoolean("SC-Entity-Section_3_goverment_entity")}</IsCRSGovEntity>
	<IsCRSIntOrg>${convertYesNoToBoolean("SC-Entity-Section_3_international_org")}</IsCRSIntOrg>
	<IsCRSCentralBank>${convertYesNoToBoolean("SC-Entity-Section_3_central_bank")}</IsCRSCentralBank>
	<IsCRSBoardParRetFund>${convertYesNoToBoolean("SC-Entity-Section_3_broad_participation_retirement_fund")}</IsCRSBoardParRetFund>
	<IsCRSNarrowParRetFund>${convertYesNoToBoolean("SC-Entity-Section_3_narrow_participation_retirement_fund")}</IsCRSNarrowParRetFund>
	<IsCRSPenFundGovEntityIntOrgORCentralBank>${convertYesNoToBoolean("SC-Entity-Section_3_pension_fund")}</IsCRSPenFundGovEntityIntOrgORCentralBank>
	<IsCRSExemptCollectiveInvVehicle>${convertYesNoToBoolean("SC-Entity-Section_3_exempt_collective_investment")}</IsCRSExemptCollectiveInvVehicle>
	<IsCRSTrust>${convertYesNoToBoolean("SC-Entity-Section_3_trust_trustee")}</IsCRSTrust>
	<IsCRSQualifiedCCI>${convertYesNoToBoolean("SC-Entity-Section_3_qualified_credit_card_issuer")}</IsCRSQualifiedCCI>
	<IsCRSOtherEntity>${convertYesNoToBoolean("SC-Entity-Section_3_other_entity")}</IsCRSOtherEntity>
	<IsCRSFINonParJur>${convertYesNoToBoolean("SC-Entity-Section_3_financial_institution_resident_in_a_non_participating_jurisdiction_under_crs_non_reporting")}</IsCRSFINonParJur>
	<IsCRS_PMIE>${convertYesNoToBoolean("SC-Entity-Section_3_investment_entity_located_in_a_non_participating_jurisdiction_under_crs_managed_by_another_financial_institution")}</IsCRS_PMIE>
	<IsCRS_IE>${convertYesNoToBoolean("SC-Entity-Section_3_investment_entity")}</IsCRS_IE>
	<IsCRS_CIV>${convertYesNoToBoolean("SC-Entity-Section_3_investment_entity_managed_by_widely_held_civ")}</IsCRS_CIV>
	<IsCRS_PentionFund>${convertYesNoToBoolean("SC-Entity-Section_3_investment_entity_managed_by_pension_fund")}</IsCRS_PentionFund>
	<IsCRSOtherInvEntity>${convertYesNoToBoolean("SC-Entity-Section_3_other_investment_entity")}</IsCRSOtherInvEntity>
	<IsCRSOtherFIincludingDFI>${convertYesNoToBoolean("SC-Entity-Section_3_non_financial_institution")}</IsCRSOtherFIincludingDFI>
	<CRSSpecifyDomesticLaw>${getValue("SC-Entity-Section_3_other_text")}</CRSSpecifyDomesticLaw>	
	<CRSNameOfStockExchangeTraded>${getValue("SC-Entity-Section_3_stock_exchange_name")}</CRSNameOfStockExchangeTraded>
	<CRSNameOfRegularlyTradedCorp>${getValue("SC-Entity-Section_3_regularly_traded_corporation_name")}</CRSNameOfRegularlyTradedCorp>
	<CRSFINonParJurControllingPerson1>${getValue("SC-Entity-Controlling_person_1")}</CRSFINonParJurControllingPerson1>
	<CRSFINonParJurControllingPerson2>${getValue("SC-Entity-Controlling_person_2")}</CRSFINonParJurControllingPerson2>
	<CRSFINonParJurControllingPerson3>${getValue("SC-Entity-Controlling_person_3")}</CRSFINonParJurControllingPerson3>
	<CRSFINonParJurControllingPerson4>${getValue("SC-Entity-Controlling_person_4")}</CRSFINonParJurControllingPerson4>
	<IsCRSActiveNFE>${convertYesNoToBoolean("SC-Entity-Section_3_active_nfe")}</IsCRSActiveNFE>
	<IsCRSCorRegularlyTraded>${convertYesNoToBoolean("SC-Entity-Section_3_regularly_traded_corporation")}</IsCRSCorRegularlyTraded>
	<IsCRSGovernmentEntity>${convertYesNoToBoolean("SC-Entity-Section_3_active_nfe_goverment_entity")}</IsCRSGovernmentEntity>
	<IsCRSInternationalOrganisation>${convertYesNoToBoolean("SC-Entity-Section_3_active_nfe_international_org")}</IsCRSInternationalOrganisation>
	<IsOtherThan1and4>${convertYesNoToBoolean("SC-Entity-Section_3_active_nfe_other")}</IsOtherThan1and4>
	<isPassiveNFEUnderCRS>${convertYesNoToBoolean("SC-Entity-Section_3_passive_nfe")}</isPassiveNFEUnderCRS>
	<passiveNFFEControllers>
	     <BOTitleName>${getValue("SC-Entity-Controlling_person_title")}</BOTitleName>
		<BOFirstName>${getValue("SC-Entity-Controlling_person_first_name")}</BOFirstName>
		<BOCapacity>${getValue("SC-Entity-Section_5_capacity")}</BOCapacity>
		<BOSurName>${getValue("SC-Entity-Controlling_person_last_name")}</BOSurName>
		<BOMiddleName>${getValue("SC-Entity-Controlling_person_middle_name")}</BOMiddleName>
		<BOPermAddressPropertyName>${getValue("SC-Entity-Controlling_person_address_street")}</BOPermAddressPropertyName>
		<Address>${getValue("SC-Entity-Controlling_person_address_street2")}</Address>
		<AddressCity>${getValue("SC-Entity-Controlling_person_address_city")}, ${getValue("SC-Entity-Controlling_person_address_state")}</AddressCity>
		<Country_ISOCode>${getValue("SC-Entity-Controlling_person_address_country")}</Country_ISOCode>
		<AddressPostCode>${getValue("SC-Entity-Controlling_person_address_postal")}</AddressPostCode>
		<BOMailingAddressPropertyName>${getValue("SC-Entity-Controlling_person_mailing_address_street")}</BOMailingAddressPropertyName>
		<MailingAddressPostCode>${getValue("SC-Entity-Controlling_person_mailing_address_postal")}</MailingAddressPostCode>
		<MailingAddressCity>${getValue("SC-Entity-Controlling_person_mailing_address_city")}, ${getValue("SC-Entity-Controlling_person_mailing_address_state")}</MailingAddressCity>
		<MailingAddressStreet>${getValue("SC-Entity-Controlling_person_mailing_address_street2")}</MailingAddressStreet>
		<MailingAddressCountry_ISO>${getValue("SC-Entity-Controlling_person_mailing_address_country")}</MailingAddressCountry_ISO>
		<DateOfBirthFormatted>${formatSelfCertDate("SC-Entity-Controlling_person_dob")}</DateOfBirthFormatted>
		<DateOfBirth>${getValue("SC-Entity-Controlling_person_dob")}</DateOfBirth>
		<DateOfBirthOnForm></DateOfBirthOnForm>
		<BOPlaceOfBirthTown>${getValue("SC-Entity-Controlling_person_city_of_birth")}</BOPlaceOfBirthTown>
		<CountryOfBirth_ISO>${getValue("SC-Entity-Controlling_person_country_of_birth")}</CountryOfBirth_ISO>		
		<AdditionalTaxJururisdictionCountry1_ISO>${getValue("SC-Entity-Controlling_person_jurisdiction_2")}</AdditionalTaxJururisdictionCountry1_ISO>
		<AdditionalTaxJururisdictionCountry2_ISO>${getValue("SC-Entity-Controlling_person_jurisdiction_3")}</AdditionalTaxJururisdictionCountry2_ISO>
		<AdditionalTaxJururisdictionCountry3_ISO>${getValue("SC-Entity-Controlling_person_jurisdiction_4")}</AdditionalTaxJururisdictionCountry3_ISO>
		<AdditionalTIN1>${getValue("SC-Entity-Controlling_person_tax_id_2")}</AdditionalTIN1>
		<AdditionalTIN2>${getValue("SC-Entity-Controlling_person_tax_id_3")}</AdditionalTIN2>
		<AdditionalTIN3>${getValue("SC-Entity-Controlling_person_tax_id_4")}</AdditionalTIN3>
		<TINUnavailableReason1>${getValue("SC-Entity-Controlling_person_tin_reason_2")}</TINUnavailableReason1>
		<TINUnavailableReason2>${getValue("SC-Entity-Controlling_person_tin_reason_3")}</TINUnavailableReason2>
		<TINUnavailableReason3>${getValue("SC-Entity-Controlling_person_tin_reason_4")}</TINUnavailableReason3>		
		<AdditionalReferenceType1>${getValue("SC-Entity-Controlling_person_tax_reference_type_2")}</AdditionalReferenceType1>
		<AdditionalReferenceType2>${getValue("SC-Entity-Controlling_person_tax_reference_type_3")}</AdditionalReferenceType2>
		<AdditionalReferenceType3>${getValue("SC-Entity-Controlling_person_tax_reference_type_4")}</AdditionalReferenceType3>		
		<ControllingPersonTaxResidencyCountry>${getValue("SC-Entity-Section_4_jurisdiction_1")}</ControllingPersonTaxResidencyCountry>
		<ControllingPersonTaxResidencyTINType>${getValue("SC-Entity-Section_4_tax_reference_number")}</ControllingPersonTaxResidencyTINType>
		<ControllingPersonTIN>${getValue("SC-Entity-Section_4_tax_id")}</ControllingPersonTIN>
		<cbControllingPersonIsTinUnavailable>${convertYesNoToBoolean("SC-Entity-Section_4_unable_to_procure_tin")}</cbControllingPersonIsTinUnavailable>
		<ControllingPersonTInUnavailableReason>${getValue("SC-Entity-Section_4_reason_non_availability_of_tin")}</ControllingPersonTInUnavailableReason>
		<cbControllingPersonTaxResidentYes>${convertYesNoToBoolean("SC-Entity-Section_4_sole_residency_yes")}</cbControllingPersonTaxResidentYes>
		<cbControllingPersonTaxResidentNo>${convertYesNoToBoolean("SC-Entity-Section_4_sole_residency_no")}</cbControllingPersonTaxResidentNo>		
		<ControllingPerson1_a>${convertYesNoToBoolean("SC-Entity-Controlling_person_type_owner")}</ControllingPerson1_a>
		<ControllingPerson1_aPercentage>${getValue("SC-Entity-Controlling_person_owner_percentage")}</ControllingPerson1_aPercentage>
		<ControllingPerson1_b>${convertYesNoToBoolean("SC-Entity-Controlling_person_by_other_means")}</ControllingPerson1_b>
		<ControllingPerson1_c>${convertYesNoToBoolean("SC-Entity-Controlling_person_senior_managing")}</ControllingPerson1_c>
		<ControllingPerson1_d>${convertYesNoToBoolean("SC-Entity-Controlling_person_settlor")}</ControllingPerson1_d>
		<ControllingPerson1_e>${convertYesNoToBoolean("SC-Entity-Controlling_person_trustee")}</ControllingPerson1_e>
		<ControllingPerson1_f>${convertYesNoToBoolean("SC-Entity-Controlling_person_protector")}</ControllingPerson1_f>
		<ControllingPerson1_g>${convertYesNoToBoolean("SC-Entity-Controlling_person_beneficiary")}</ControllingPerson1_g>
		<ControllingPerson1_h>${convertYesNoToBoolean("SC-Entity-Controlling_person_other")}</ControllingPerson1_h>
		<ControllingPerson1_i>${convertYesNoToBoolean("SC-Entity-Controlling_person_settlor_equivalent")}</ControllingPerson1_i>
		<ControllingPerson1_j>${convertYesNoToBoolean("SC-Entity-Controlling_person_trustee_equivalent")}</ControllingPerson1_j>
		<ControllingPerson1_k>${convertYesNoToBoolean("SC-Entity-Controlling_person_protector_equivalent")}</ControllingPerson1_k>
		<ControllingPerson1_l>${convertYesNoToBoolean("SC-Entity-Controlling_person_beneficiary_equivalent")}</ControllingPerson1_l>
		<ControllingPerson1_m>${convertYesNoToBoolean("SC-Entity-Controlling_person_other_equivalent")}</ControllingPerson1_m>
		<ControllingPerson1_m_other>${getValue("SC-Entity-Section_4_other_text_2")}</ControllingPerson1_m_other>
		<ControllingPerson1_h_other>${getValue("SC-Entity-Section_4_other_text_1")}</ControllingPerson1_h_other>
		<FillText1>${getValue("SC-Entity-Legal_entity")}</FillText1>
		<ControllingPerson1_PrintName>${getValue("SC-Entity-Section_5_signer_printed_name")}</ControllingPerson1_PrintName>
		<ControllingPerson1_Date>${formatSelfCertDate("SC-Entity-Section_5_signature_date")}</ControllingPerson1_Date>

		<ControllingPerson1_Capacity>${getValue("SC-Entity-Section_5_capacity")}</ControllingPerson1_Capacity>
	</passiveNFFEControllers>
	<Tag>
		<CreatedByApp>Extractor</CreatedByApp>
	</Tag>
	${originalFileXml}
</Deloitte_US_Entity_2024>`;

  const escapedXml = escapeXmlText(xml);
  return escapedXml;
}
